import {t, Trans} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {tryParseUrl} from '../../../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {AsyncStatusesEnum} from '../../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {TextBox} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {useLocalize} from '../../../../../../../srcCommon/internationalization/internationalizationContext';
import {CreateFormInputDisplay} from '../../../../../../components/common/createFormInputs/createFormInputDisplay';
import {AsyncLoader} from '../../../../../../components/common/loaders/asyncLoader';
import {useLinkNavigation} from '../../../../../../components/common/navigation/useLinkNavigation';
import {Skeleton} from '../../../../../../components/common/skeleton';
import {useAppDispatch} from '../../../../../../helpers/reduxHelpers';
import {
  ApplicationEntryPointActionSdkVersionsEnum,
  ApplicationEntryPointActionTypesEnum,
} from '../../../../../../models/api/applicationEntryPointActionModel';
import {ApplicationEntryPointTypesEnum} from '../../../../../../models/api/applicationEntryPointModel';
import {LocalApplicationEntryPointModel} from '../../../../../../models/api/localApplicationEntryPointModel';
import {LocalApplicationModel} from '../../../../../../models/api/localApplicationModel';
import {buildSettingsDevelopersPath} from '../../../../../../models/url/settingsUrlHelpers';
import {
  SettingsDevelopersPagesEnum,
  SettingsRootPagesEnum,
} from '../../../../../../models/url/settingsUrlModel';
import {
  createLocalApplicationEntryPointAsyncAction,
  fetchLocalApplicationEntryPointsAsyncAction,
  updateLocalApplicationEntryPointAsyncAction,
} from '../../../../../../store/data/localApplications/localApplicationsActions';
import {SettingsBannersEnum, useSettingsBanners} from '../../../../../banners/settingsBannersContext';
import {DevelopersApplicationInboxSidePanelEntryPointActionFeaturesInput} from './developerInboxSidePanelEntryPointActionFeaturesInput';

/*
 * Props.
 */

interface DevelopersApplicationFeatureInboxSidePanelEntryPointProps {
  application: LocalApplicationModel;
  entryPoint?: LocalApplicationEntryPointModel;
  entryPointAsyncStatus?: AsyncStatusesEnum;
}

/*
 * Style.
 */

const StyledEntryPointWrapperDiv = styled.div`
  max-width: 600px;
  width: 100%;
  padding: 0 1px 64px 1px;
  overflow: auto;
`;

const StyledErrorWrapperDiv = styled.div`
  flex: 1;
  padding: 32px;
`;

const StyledFooterDiv = styled.div`
  border-top: 1px solid ${(p) => p.theme.alphas.gray10};
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 8px;
  background: ${(p) => p.theme.greys.white};
`;

/*
 * Component.
 */

export const DevelopersApplicationFeatureInboxSidePanelEntryPoint: FC<
  DevelopersApplicationFeatureInboxSidePanelEntryPointProps
> = (props) => {
  const {application, entryPoint, entryPointAsyncStatus} = props;
  const dispatch = useAppDispatch();
  const localize = useLocalize();
  const {navigateToLink} = useLinkNavigation();
  const {showBanner} = useSettingsBanners();

  const [endpoint, setEndpoint] = useState(entryPoint?.action?.endpoint || '');
  const [features, setFeatures] = useState(entryPoint?.action?.features || []);

  const [isSaving, setIsSaving] = useState(false);

  // Watch the local applications entry point, if it changes update the view.
  useEffect(() => {
    setEndpoint(entryPoint?.action?.endpoint || '');
    setFeatures(entryPoint?.action?.features || []);
  }, [entryPoint?.action]);

  const navigateBackToFeatures = () => {
    navigateToLink(
      buildSettingsDevelopersPath({
        rootPage: SettingsRootPagesEnum.DEVELOPERS,
        subPage: SettingsDevelopersPagesEnum.APPLICATION_FEATURES,
        applicationId: application.id,
      }),
    );
  };

  const onSaveClick = async () => {
    setIsSaving(true);

    if (entryPoint && entryPoint.action) {
      try {
        await dispatch(
          updateLocalApplicationEntryPointAsyncAction(
            application.id,
            entryPoint.id,
            ApplicationEntryPointTypesEnum.INBOX_SIDE_PANEL,
            {
              endpoint,
              features,
              type: entryPoint.action.type,
              sdkVersion: entryPoint.action.sdkVersion,
              label: undefined,
            },
          ),
        );
        navigateBackToFeatures();
      } catch {
        setIsSaving(false);
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: (
            <Trans id="developers-application-feature-entry-point-error-updating-message">
              Error updating plugin. Please try again.
            </Trans>
          ),
        });
      }
    } else {
      try {
        await dispatch(
          createLocalApplicationEntryPointAsyncAction(
            application.id,
            ApplicationEntryPointTypesEnum.INBOX_SIDE_PANEL,
            {
              endpoint,
              features,
              type: ApplicationEntryPointActionTypesEnum.OPEN_SIDE_PANEL,
              sdkVersion: ApplicationEntryPointActionSdkVersionsEnum.v1_x,
              label: undefined,
            },
          ),
        );
        navigateBackToFeatures();
      } catch {
        setIsSaving(false);
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: (
            <Trans id="developers-application-feature-entry-point-error-creating-message">
              Error creating plugin. Please try again.
            </Trans>
          ),
        });
      }
    }
  };

  if (entryPointAsyncStatus === AsyncStatusesEnum.LOADING) {
    return <Skeleton height="300px" width="600px" />;
  }
  // If we failed to fetch the entry points.
  if (entryPointAsyncStatus === AsyncStatusesEnum.FAILED) {
    return (
      <StyledErrorWrapperDiv>
        <AsyncLoader
          status={AsyncStatusesEnum.FAILED}
          onRetryClick={() => dispatch(fetchLocalApplicationEntryPointsAsyncAction(application.id))}
        />
      </StyledErrorWrapperDiv>
    );
  }

  const errorMessage = computeErrorMessage(endpoint);
  return (
    <StyledEntryPointWrapperDiv>
      <CreateFormInputDisplay
        isRequired
        title={<Trans id="developers-application-feature-entry-point-description">Side panel URL</Trans>}
        input={
          <TextBox
            value={endpoint}
            onChange={(value) => setEndpoint(value)}
            isRequired
            noMargin
            message={
              <Trans id="developers-application-feature-entry-point-description-message">
                Add your Side panel UI link
              </Trans>
            }
            placeholder={localize(
              t(
                'developers-application-feature-entry-point-description-placeholder',
              )`ie. https://example.com/plugin`,
            )}
            errorMessage={errorMessage}
            hasError={Boolean(errorMessage)}
          />
        }
      />

      <DevelopersApplicationInboxSidePanelEntryPointActionFeaturesInput
        features={features}
        onSelectedFeaturesChange={setFeatures}
      />

      <StyledFooterDiv>
        <ButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={navigateBackToFeatures} isDisabled={isSaving}>
            <Trans id="developers-application-entry-point-cancel-button">Cancel</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            isDisabled={!endpoint || Boolean(errorMessage) || isSaving}
            onClick={onSaveClick}
          >
            {!entryPoint && <Trans id="developers-application-entry-point-create-button">Create</Trans>}
            {entryPoint && <Trans id="developers-application-entry-point-update-button">Update</Trans>}
          </Button>
        </ButtonGroup>
      </StyledFooterDiv>
    </StyledEntryPointWrapperDiv>
  );
};

/*
 * Helpers.
 */

function computeErrorMessage(url: string) {
  if (!url) {
    return null;
  }
  const parseUrl = tryParseUrl(url);
  if (!parseUrl) {
    return (
      <Trans id="developers-application-feature-entry-point-description-error-message-invalid-url">
        Invalid URL
      </Trans>
    );
  }
  return null;
}
